import React from 'react';

import GlobeIcon from '@atlaskit/icon/core/globe';
import {
	ConfluenceIcon,
	ConfluenceDataCenterIcon,
	JiraIcon,
	JiraDataCenterIcon,
} from '@atlaskit/logo-next';
import { Inline, xcss } from '@atlaskit/primitives-next';

import { ProductVariant, PRODUCT_VARIANTS, ProductName, PRODUCT_NAMES } from '../types';

type ProductConfig = Record<ProductVariant, React.ReactNode>;

const fallbackIconStyles = xcss({
	width: '32px',
	height: '32px',
	borderRadius: '6px',
	backgroundColor: 'color.background.accent.gray.subtler',
});

interface ProductIconProps {
	productName: ProductName | null;
	variant: ProductVariant;
}

const PRODUCT_CONFIG: Record<ProductName, ProductConfig> = {
	[PRODUCT_NAMES.JIRA]: {
		[PRODUCT_VARIANTS.SOURCE]: <JiraDataCenterIcon appearance="brand" size="medium" />,
		[PRODUCT_VARIANTS.DESTINATION]: (
			<JiraIcon appearance="brand" size="medium" shouldUseNewLogoDesign />
		),
	},
	[PRODUCT_NAMES.CONFLUENCE]: {
		[PRODUCT_VARIANTS.SOURCE]: <ConfluenceDataCenterIcon appearance="brand" size="medium" />,
		[PRODUCT_VARIANTS.DESTINATION]: (
			<ConfluenceIcon appearance="brand" size="medium" shouldUseNewLogoDesign />
		),
	},
};

const FALLBACK_ICON = (
	<Inline alignBlock="center" alignInline="center" xcss={fallbackIconStyles}>
		<GlobeIcon label="globe" size="medium" />
	</Inline>
);

export const getIcon = (
	productType: ProductName | null,
	variant: ProductVariant,
): React.ReactNode => {
	if (!productType || !(productType in PRODUCT_CONFIG)) {
		return FALLBACK_ICON;
	}

	return PRODUCT_CONFIG[productType][variant] ?? FALLBACK_ICON;
};

export const ProductIcon = ({
	productName: productType,
	variant,
}: ProductIconProps): React.ReactNode => {
	return getIcon(productType, variant);
};
